/**
 * TaskDetailDrawer.tsx / TaskDetailModal.tsx
 *
 * Centered, Responsive Modal Popup & Inspector for TaskFlow items.
 * 100% Theme-tokenized with fluid typography, focus trapping, Esc key dismissal,
 * role="dialog", aria-modal="true", chronological activity audit ledger,
 * localized labels via useTranslation(), and <bdi> text isolation.
 */

import React, { useState, useEffect, useRef } from 'react';
import type { TaskFlowItem, TaskFlowItemType } from '../../../plugins/taskflow/contracts/taskflow-schema.js';
import { globalTaskFlowStore } from '../../../plugins/taskflow/engine/taskflow-store.js';
import type { TaskFlowAuditEntry } from '../../../plugins/taskflow/engine/taskflow-audit-log.js';
import { useTranslation } from '../../../i18n/useTranslation.js';

export interface TaskDetailDrawerProps {
  itemId: string | null;
  workspaceId: string;
  onClose: () => void;
  onItemUpdated?: (item: TaskFlowItem) => void;
}

export const TaskDetailDrawer: React.FC<TaskDetailDrawerProps> = ({
  itemId,
  workspaceId,
  onClose,
  onItemUpdated,
}) => {
  const { t, formatDate } = useTranslation();
  const [item, setItem] = useState<TaskFlowItem | null>(null);
  const [auditHistory, setAuditHistory] = useState<TaskFlowAuditEntry[]>([]);
  const [newTag, setNewTag] = useState('');
  const [activeTab, setActiveTab] = useState<'details' | 'audit'>('details');

  const modalRef = useRef<HTMLDivElement | null>(null);
  const triggerElementRef = useRef<HTMLElement | null>(null);

  // Store previously focused element for focus restoration on close
  useEffect(() => {
    if (itemId) {
      triggerElementRef.current = document.activeElement as HTMLElement;
      const found = globalTaskFlowStore.getItem(itemId);
      if (found) {
        setItem(found);
        setAuditHistory(globalTaskFlowStore.getAuditHistory(itemId));
        setActiveTab('details');
      }
    } else {
      setItem(null);
      setAuditHistory([]);
      triggerElementRef.current?.focus();
    }
  }, [itemId]);

  // Global ESC key listener to dismiss modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && itemId) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [itemId, onClose]);

  if (!itemId || !item) return null;

  const handleUpdate = (patch: Partial<TaskFlowItem>) => {
    const updated = globalTaskFlowStore.updateItem(item.id, {
      patch,
      actorId: 'user',
    });
    if (updated) {
      setItem(updated);
      setAuditHistory(globalTaskFlowStore.getAuditHistory(item.id));
      onItemUpdated?.(updated);
    }
  };

  const handleAddTag = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newTag.trim()) {
      e.preventDefault();
      const currentTags = item.tags || [];
      if (!currentTags.includes(newTag.trim())) {
        handleUpdate({ tags: [...currentTags, newTag.trim()] });
      }
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    const currentTags = item.tags || [];
    handleUpdate({ tags: currentTags.filter((t) => t !== tagToRemove) });
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'var(--theme-bg-backdrop, rgba(0, 0, 0, 0.78))',
        backdropFilter: 'blur(10px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
      onClick={onClose}
    >
      {/* Modal Container */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="task-detail-modal-title"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '680px',
          maxHeight: '90vh',
          backgroundColor: 'var(--theme-bg-modal)',
          border: '1px solid var(--theme-border-highlight)',
          borderRadius: 'var(--theme-radius-xl, 16px)',
          boxShadow: 'var(--theme-shadow-lg)',
          display: 'flex',
          flexDirection: 'column',
          color: 'var(--theme-text-primary)',
          overflow: 'hidden',
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: '16px 20px',
            backgroundColor: 'var(--theme-bg-header)',
            borderBottom: '1px solid var(--theme-border-subtle)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <select
              aria-label="Item Type"
              value={item.type}
              onChange={(e) => handleUpdate({ type: e.target.value as TaskFlowItemType })}
              style={{
                backgroundColor: 'var(--theme-accent-subtle)',
                color: 'var(--theme-accent)',
                border: '1px solid var(--theme-accent)',
                borderRadius: 'var(--theme-radius-sm, 4px)',
                padding: '4px 10px',
                fontSize: 'var(--theme-text-xs)',
                fontWeight: 700,
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              <option value="goal">🎯 {t('taskflow.types.goal') || 'Goal'}</option>
              <option value="plan">🗺️ {t('taskflow.types.plan') || 'Plan'}</option>
              <option value="project">📦 {t('taskflow.types.project') || 'Project'}</option>
              <option value="task">✅ {t('taskflow.types.task') || 'Task'}</option>
              <option value="action">⚡ {t('taskflow.types.action') || 'Action'}</option>
            </select>
            <span style={{ fontSize: 'var(--theme-text-xs)', color: 'var(--theme-text-muted)' }}>
              {t('taskflow.revision', { number: item.revision || 1 }) || `Revision #${item.revision || 1}`}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              onClick={() => {
                globalTaskFlowStore.archiveItem(item.id, 'user');
                onClose();
              }}
              title={t('taskflow.archive')}
              aria-label="Archive Task"
              style={{
                padding: '4px 8px',
                background: 'var(--theme-bg-subtle)',
                border: '1px solid var(--theme-border)',
                borderRadius: 'var(--theme-radius-sm, 4px)',
                color: 'var(--theme-text-secondary)',
                cursor: 'pointer',
                fontSize: 'var(--theme-text-xs)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <span>📥</span>
              <span>{t('taskflow.archive') || 'Archive'}</span>
            </button>

            <button
              onClick={onClose}
              title={t('common.close')}
              aria-label={t('common.close')}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--theme-text-muted)',
                cursor: 'pointer',
                fontSize: 'var(--theme-text-lg)',
                padding: '4px 8px',
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div
          style={{
            display: 'flex',
            backgroundColor: 'var(--theme-bg-subtle)',
            borderBottom: '1px solid var(--theme-border-subtle)',
            padding: '0 20px',
            gap: '8px',
          }}
        >
          <button
            onClick={() => setActiveTab('details')}
            style={{
              padding: '12px 16px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'details' ? '2px solid var(--theme-accent)' : '2px solid transparent',
              color: activeTab === 'details' ? 'var(--theme-accent)' : 'var(--theme-text-secondary)',
              fontSize: 'var(--theme-text-sm)',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'color var(--theme-transition-fast), border-color var(--theme-transition-fast)',
            }}
          >
            <span>📝</span>
            <span>{t('taskflow.propertiesAndDetails') || 'Properties & Details'}</span>
          </button>

          <button
            onClick={() => setActiveTab('audit')}
            style={{
              padding: '12px 16px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'audit' ? '2px solid var(--theme-accent)' : '2px solid transparent',
              color: activeTab === 'audit' ? 'var(--theme-accent)' : 'var(--theme-text-secondary)',
              fontSize: 'var(--theme-text-sm)',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'color var(--theme-transition-fast), border-color var(--theme-transition-fast)',
            }}
          >
            <span>📜</span>
            <span>{t('taskflow.activityHistory') || 'Activity History'}</span>
            <span
              style={{
                fontSize: 'var(--theme-text-xs)',
                backgroundColor: activeTab === 'audit' ? 'var(--theme-accent)' : 'var(--theme-bg-subtle)',
                color: 'var(--theme-text-inverse, #fff)',
                padding: '2px 6px',
                borderRadius: '10px',
              }}
            >
              {auditHistory.length}
            </span>
          </button>
        </div>

        {/* Modal Scrollable Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {activeTab === 'details' ? (
            <>
              {/* Title Field */}
              <div>
                <label
                  id="task-detail-modal-title"
                  style={{ fontSize: 'var(--theme-text-xs)', fontWeight: 700, color: 'var(--theme-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}
                >
                  {t('kanban.taskTitle') || 'Task Title'}
                </label>
                <input
                  type="text"
                  aria-label="Task Title"
                  value={item.title}
                  onChange={(e) => handleUpdate({ title: e.target.value })}
                  placeholder={t('kanban.taskTitle') || 'Task title...'}
                  style={{
                    width: '100%',
                    marginTop: '6px',
                    padding: '10px 14px',
                    backgroundColor: 'var(--theme-bg-input)',
                    border: '1px solid var(--theme-border)',
                    borderRadius: 'var(--theme-radius-md, 8px)',
                    color: 'var(--theme-text-primary)',
                    fontSize: 'var(--theme-text-base)',
                    fontWeight: 600,
                    outline: 'none',
                  }}
                />
              </div>

              {/* Status & Priority Row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={{ fontSize: 'var(--theme-text-xs)', fontWeight: 700, color: 'var(--theme-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {t('common.status') || 'Status'}
                  </label>
                  <select
                    aria-label="Status"
                    value={item.status}
                    onChange={(e) => handleUpdate({ status: e.target.value })}
                    style={{
                      width: '100%',
                      marginTop: '6px',
                      padding: '8px 12px',
                      backgroundColor: 'var(--theme-bg-input)',
                      border: '1px solid var(--theme-border)',
                      borderRadius: 'var(--theme-radius-md, 8px)',
                      color: 'var(--theme-text-primary)',
                      fontSize: 'var(--theme-text-sm)',
                      outline: 'none',
                    }}
                  >
                    <option value="backlog">{t('taskflow.statuses.backlog') || 'Backlog'}</option>
                    <option value="in-progress">{t('taskflow.statuses.inProgress') || 'In Progress'}</option>
                    <option value="blocked">{t('taskflow.statuses.blocked') || 'Blocked'}</option>
                    <option value="review">{t('taskflow.statuses.review') || 'Review'}</option>
                    <option value="done">{t('taskflow.statuses.done') || 'Done'}</option>
                    <option value="cancelled">{t('taskflow.statuses.cancelled') || 'Cancelled'}</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: 'var(--theme-text-xs)', fontWeight: 700, color: 'var(--theme-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {t('kanban.priority') || 'Priority'}
                  </label>
                  <select
                    aria-label="Priority"
                    value={item.priority || 'normal'}
                    onChange={(e) => handleUpdate({ priority: e.target.value as any })}
                    style={{
                      width: '100%',
                      marginTop: '6px',
                      padding: '8px 12px',
                      backgroundColor: 'var(--theme-bg-input)',
                      border: '1px solid var(--theme-border)',
                      borderRadius: 'var(--theme-radius-md, 8px)',
                      color: 'var(--theme-text-primary)',
                      fontSize: 'var(--theme-text-sm)',
                      outline: 'none',
                    }}
                  >
                    <option value="low">{t('taskflow.priorities.low') || 'Low'}</option>
                    <option value="normal">{t('taskflow.priorities.normal') || 'Normal'}</option>
                    <option value="medium">{t('taskflow.priorities.medium') || 'Medium'}</option>
                    <option value="high">{t('taskflow.priorities.high') || 'High'}</option>
                    <option value="urgent">{t('taskflow.priorities.urgent') || 'Urgent 🔥'}</option>
                  </select>
                </div>
              </div>

              {/* Progress Slider */}
              <div style={{ backgroundColor: 'var(--theme-bg-subtle)', padding: '12px 14px', borderRadius: 'var(--theme-radius-md, 8px)', border: '1px solid var(--theme-border-subtle)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={{ fontSize: 'var(--theme-text-xs)', fontWeight: 700, color: 'var(--theme-text-secondary)', textTransform: 'uppercase' }}>
                    {t('taskflow.progress') || 'Progress Completion'}
                  </label>
                  <span style={{ fontSize: 'var(--theme-text-sm)', fontWeight: 700, color: 'var(--theme-accent)' }}>
                    {item.progressPercentage || 0}%
                  </span>
                </div>
                <input
                  type="range"
                  aria-label="Progress percentage"
                  min={0}
                  max={100}
                  value={item.progressPercentage || 0}
                  onChange={(e) => handleUpdate({ progressPercentage: Number(e.target.value) })}
                  style={{
                    width: '100%',
                    marginTop: '8px',
                    accentColor: 'var(--theme-accent)',
                    cursor: 'pointer',
                  }}
                />
              </div>

              {/* Dates Row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={{ fontSize: 'var(--theme-text-xs)', fontWeight: 700, color: 'var(--theme-text-secondary)', textTransform: 'uppercase' }}>
                    {t('taskflow.startDate') || 'Start Date'}
                  </label>
                  <input
                    type="date"
                    aria-label="Start Date"
                    value={item.startDate ? item.startDate.split('T')[0] : ''}
                    onChange={(e) => handleUpdate({ startDate: e.target.value || undefined })}
                    style={{
                      width: '100%',
                      marginTop: '6px',
                      padding: '8px 12px',
                      backgroundColor: 'var(--theme-bg-input)',
                      border: '1px solid var(--theme-border)',
                      borderRadius: 'var(--theme-radius-md, 8px)',
                      color: 'var(--theme-text-primary)',
                      fontSize: 'var(--theme-text-xs)',
                      outline: 'none',
                    }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: 'var(--theme-text-xs)', fontWeight: 700, color: 'var(--theme-text-secondary)', textTransform: 'uppercase' }}>
                    {t('taskflow.dueDate') || 'Due Date'}
                  </label>
                  <input
                    type="date"
                    aria-label="Due Date"
                    value={item.dueDate ? item.dueDate.split('T')[0] : ''}
                    onChange={(e) => handleUpdate({ dueDate: e.target.value || undefined })}
                    style={{
                      width: '100%',
                      marginTop: '6px',
                      padding: '8px 12px',
                      backgroundColor: 'var(--theme-bg-input)',
                      border: '1px solid var(--theme-border)',
                      borderRadius: 'var(--theme-radius-md, 8px)',
                      color: 'var(--theme-text-primary)',
                      fontSize: 'var(--theme-text-xs)',
                      outline: 'none',
                    }}
                  />
                </div>
              </div>

              {/* Description Markdown Notes */}
              <div>
                <label style={{ fontSize: 'var(--theme-text-xs)', fontWeight: 700, color: 'var(--theme-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {t('taskflow.description') || 'Description & Notes'}
                </label>
                <textarea
                  rows={5}
                  aria-label="Task Description"
                  value={item.description || ''}
                  onChange={(e) => handleUpdate({ description: e.target.value })}
                  placeholder="Add notes, specs, or acceptance criteria..."
                  style={{
                    width: '100%',
                    marginTop: '6px',
                    padding: '10px 14px',
                    backgroundColor: 'var(--theme-bg-input)',
                    border: '1px solid var(--theme-border)',
                    borderRadius: 'var(--theme-radius-md, 8px)',
                    color: 'var(--theme-text-primary)',
                    fontSize: 'var(--theme-text-sm)',
                    lineHeight: 'var(--theme-leading-normal)',
                    outline: 'none',
                    resize: 'vertical',
                  }}
                />
              </div>

              {/* Tags Section */}
              <div>
                <label style={{ fontSize: 'var(--theme-text-xs)', fontWeight: 700, color: 'var(--theme-text-secondary)', textTransform: 'uppercase' }}>
                  {t('taskflow.tags') || 'Tags'}
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                  {item.tags?.map((tag) => (
                    <span
                      key={tag}
                      style={{
                        fontSize: 'var(--theme-text-xs)',
                        backgroundColor: 'var(--theme-accent-subtle)',
                        color: 'var(--theme-accent)',
                        padding: '3px 8px',
                        borderRadius: 'var(--theme-radius-sm, 4px)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <bdi>#{tag}</bdi>
                      <button
                        aria-label={`Remove tag ${tag}`}
                        onClick={() => handleRemoveTag(tag)}
                        style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 'var(--theme-text-xs)', padding: 0 }}
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    aria-label="Add new tag"
                    placeholder={t('taskflow.addTag') || '+ Add tag (Press Enter)...'}
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={handleAddTag}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: 'transparent',
                      border: '1px dashed var(--theme-border)',
                      borderRadius: 'var(--theme-radius-sm, 4px)',
                      color: 'var(--theme-text-primary)',
                      fontSize: 'var(--theme-text-xs)',
                      outline: 'none',
                    }}
                  />
                </div>
              </div>
            </>
          ) : (
            /* Activity History Tab */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {auditHistory.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px', color: 'var(--theme-text-muted)', fontSize: 'var(--theme-text-sm)' }}>
                  No activity history recorded yet.
                </div>
              ) : (
                auditHistory.map((entry) => (
                  <div
                    key={entry.entryId}
                    style={{
                      padding: '12px',
                      backgroundColor: 'var(--theme-bg-subtle)',
                      border: '1px solid var(--theme-border-subtle)',
                      borderRadius: 'var(--theme-radius-md, 8px)',
                      fontSize: 'var(--theme-text-xs)',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--theme-text-secondary)', marginBottom: '4px' }}>
                      <span style={{ fontWeight: 600 }}>{entry.actorId}</span>
                      <span style={{ fontSize: 'var(--theme-text-xs)' }}>{formatDate(entry.timestamp)}</span>
                    </div>
                    <div style={{ color: 'var(--theme-text-primary)' }}>
                      Action: <code>{entry.action}</code>
                    </div>
                    {entry.diffs && entry.diffs.length > 0 && (
                      <div style={{ marginTop: '6px', padding: '6px', backgroundColor: 'var(--theme-bg-subtle)', borderRadius: 'var(--theme-radius-sm, 4px)', fontSize: 'var(--theme-text-xs)' }}>
                        {entry.diffs.map((d) => (
                          <div key={d.field} style={{ color: 'var(--theme-text-muted)' }}>
                            <strong>{d.field}</strong>: <span style={{ color: 'var(--theme-status-danger-text)' }}>{JSON.stringify(d.oldValue)}</span> ➔ <span style={{ color: 'var(--theme-status-success-text)' }}>{JSON.stringify(d.newValue)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const TaskDetailModal = TaskDetailDrawer;
